/**
 * Places Service - Abstraction layer for fetching place data
 *
 * This service provides a clean interface for fetching places data,
 * allowing easy switching between:
 * - Static JSON import (current)
 * - REST API (future)
 * - GraphQL (future)
 * - Other data sources
 *
 * To switch to API-based fetching:
 * 1. Implement fetchPlacesFromApi function
 * 2. Change FETCH_MODE to 'api'
 * 3. Set API_BASE_URL
 */

import type { Place, PlaceLayer } from '../types/Place';
import { LAYER_CONFIGS } from '../constants/layerConfig';
import { getAppConfig } from '../config/app.config';
import { getStoredToken } from './apiClient';
import staticPlacesData from '../data/places.json';

// Data source configuration from app.config.ts (single source of truth)
const dataConfig = getAppConfig().data;
const FETCH_MODE = dataConfig.fetchMode;
const API_BASE_URL = dataConfig.apiBaseUrl;

// Derive valid layers from centralized config to stay in sync
const VALID_LAYERS: PlaceLayer[] = LAYER_CONFIGS.map(config => config.id);

/**
 * Validates a single place object has all required fields with correct types
 * @throws Error if validation fails
 */
export function validatePlace(place: unknown, index: number): Place {
  if (!place || typeof place !== 'object') {
    throw new Error(`Place at index ${index} is not an object`);
  }

  const p = place as Record<string, unknown>;

  // Required non-empty string fields
  const requiredNonEmptyStrings = ['id', 'location', 'date', 'dateSort', 'country'] as const;
  for (const field of requiredNonEmptyStrings) {
    if (typeof p[field] !== 'string' || p[field] === '') {
      throw new Error(`Place at index ${index}: "${field}" must be a non-empty string`);
    }
  }

  // Hook can be empty string but must be a string
  if (typeof p.hook !== 'string') {
    throw new Error(`Place at index ${index}: "hook" must be a string`);
  }

  // Coordinates must be [number, number]
  if (!Array.isArray(p.coordinates) || p.coordinates.length !== 2) {
    throw new Error(`Place at index ${index}: "coordinates" must be [lat, lng] array`);
  }
  if (typeof p.coordinates[0] !== 'number' || typeof p.coordinates[1] !== 'number') {
    throw new Error(`Place at index ${index}: "coordinates" must contain numbers`);
  }

  // Photos must be string array
  if (!Array.isArray(p.photos)) {
    throw new Error(`Place at index ${index}: "photos" must be an array`);
  }
  if (!p.photos.every((photo: unknown) => typeof photo === 'string')) {
    throw new Error(`Place at index ${index}: "photos" must contain only strings`);
  }

  // Order must be a number
  if (typeof p.order !== 'number') {
    throw new Error(`Place at index ${index}: "order" must be a number`);
  }

  // Layer must be a valid PlaceLayer
  if (!VALID_LAYERS.includes(p.layer as PlaceLayer)) {
    throw new Error(`Place at index ${index}: "layer" must be one of: ${VALID_LAYERS.join(', ')}`);
  }

  // dateDisplay is optional but must be string if present
  if (p.dateDisplay !== undefined && typeof p.dateDisplay !== 'string') {
    throw new Error(`Place at index ${index}: "dateDisplay" must be a string if provided`);
  }

  return place as Place;
}

/**
 * Validates an array of places
 * @throws Error if validation fails
 */
export function validatePlaces(data: unknown): Place[] {
  if (!Array.isArray(data)) {
    throw new Error('Places data must be an array');
  }

  if (data.length === 0) {
    throw new Error('Places data cannot be empty');
  }

  return data.map((place, index) => validatePlace(place, index));
}

/**
 * Sorts places by their order field
 */
export function sortPlaces(places: Place[]): Place[] {
  return [...places].sort((a, b) => a.order - b.order);
}

/**
 * Computes statistics for a set of places
 */
export function computeStats(places: Place[]): { countriesCount: number; placesCount: number } {
  return {
    countriesCount: new Set(places.map(p => p.country)).size,
    placesCount: places.length,
  };
}

/**
 * Result of fetching places data
 */
export interface FetchPlacesResult {
  places: Place[];
  stats: { countriesCount: number; placesCount: number };
}

/**
 * Fetches places from static JSON import
 */
async function fetchPlacesStatic(): Promise<FetchPlacesResult> {
  // Use the statically imported data (same as fetchPlacesSync)
  // This avoids duplicate imports in the bundle
  const validatedPlaces = validatePlaces(staticPlacesData);
  const sortedPlaces = sortPlaces(validatedPlaces);

  return {
    places: sortedPlaces,
    stats: computeStats(sortedPlaces),
  };
}

/**
 * API response type from backend
 */
interface PlacesApiResponse {
  success: boolean;
  data: Place[];
  meta: {
    total: number;
    page: number;
    pageSize: number;
  };
}

/**
 * Fetches places from API with optional mapId filter
 * @param mapId - Optional map ID to filter places by
 */
async function fetchPlacesFromApi(mapId?: string): Promise<FetchPlacesResult> {
  // Build query params
  const params = new URLSearchParams({
    pageSize: '1000',
    sortBy: 'order',
    sortOrder: 'asc',
  });
  if (mapId) {
    params.set('mapId', mapId);
  }

  // Build headers with auth token
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const token = getStoredToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}/places?${params.toString()}`, {
    headers,
  });

  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`);
  }

  const apiResponse: PlacesApiResponse = await response.json();
  const validatedPlaces = validatePlaces(apiResponse.data);
  const sortedPlaces = sortPlaces(validatedPlaces);

  return {
    places: sortedPlaces,
    stats: computeStats(sortedPlaces),
  };
}

/**
 * Main function to fetch places data
 * Uses the configured FETCH_MODE to determine data source
 * @param mapId - Optional map ID to filter places by (only used in API mode)
 */
export async function fetchPlaces(mapId?: string): Promise<FetchPlacesResult> {
  switch (FETCH_MODE) {
    case 'api':
      return fetchPlacesFromApi(mapId);
    case 'static':
    default:
      return fetchPlacesStatic();
  }
}

/**
 * Synchronous version for immediate data availability
 * Uses statically imported JSON data
 */
export function fetchPlacesSync(): FetchPlacesResult {
  const validatedPlaces = validatePlaces(staticPlacesData);
  const sortedPlaces = sortPlaces(validatedPlaces);

  return {
    places: sortedPlaces,
    stats: computeStats(sortedPlaces),
  };
}
